import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AdminPageHeader, Form, AdminFormAction } from '@ui';
import {
  DEFAULT_OCS_CONSOLE_VALUES,
  OcsConsoleForm,
  NewOcsConsoleForm,
  ocsConsoleFormSchema,
} from '@lp/features/admin/forms/OcsConsoleForm';
import { useContext } from 'react';
import { AuthContext } from '@lp/core/auth';
import { useCreateOcsConsoleMutation } from '@/queries/ocs-consoles';
import { useUploadFile } from '@/hooks/useUploadFile';
import { retrieveUnifiedError } from '@/api/utils';
import { AdminPermissions } from '@/permissions';
import { FileType } from '@/queries/file';

export const OcsConsolesCreate = () => {
  const navigate = useNavigate();
  const { mutateAsync } = useCreateOcsConsoleMutation();
  const { uploadFiles } = useUploadFile(FileType.Resource);
  const { checkPermission } = useContext(AuthContext);

  const hasCreatePermissions = checkPermission(AdminPermissions.OcsConsolesCreate);

  const title = 'Create new OCS Console';
  const backToListLink = `/admin/devices/ocs_consoles`;

  const handleBackToList = () => {
    navigate(backToListLink);
  };

  const handleSubmit = async (data: NewOcsConsoleForm) => {
    try {
      let uploadedFile;

      if (data.image) {
        uploadedFile = await uploadFiles([data.image]);
      }

      await mutateAsync({
        ocsConsoleName: data.ocsConsoleName,
        serialNumber: data.serialNumber,
        caseId: data.caseId,
        nopHub: data.nopHub,
        organType: data.organType,
        status: data.status,
        isInMaintenance: data.isInMaintenance,
        logoPath: uploadedFile ? uploadedFile[0].fileName : undefined,
        trialProgramId: data.trialProgramId || null,
      });

      toast.success(`OCS Console created successfully.`);

      handleBackToList();
    } catch (e) {
      const error = retrieveUnifiedError(e);
      toast.error(error.message);
    }
  };

  return (
    <Form<NewOcsConsoleForm>
      defaultValues={DEFAULT_OCS_CONSOLE_VALUES}
      schema={ocsConsoleFormSchema}
      onSubmit={handleSubmit}
    >
      <div className="flex flex-col gap-9">
        <AdminPageHeader title={title} backToListLink={backToListLink}>
          <AdminFormAction type="create" handleClick={handleBackToList} hasCreatePermissions={hasCreatePermissions} />
        </AdminPageHeader>
        <OcsConsoleForm isReadOnly={!hasCreatePermissions} />
      </div>
    </Form>
  );
};
